<template>
	<div>
		<div ref="dropDownWrapper" class="tw group relative text-base">
			<select
				:id="name"
				ref="nativeSelect"
				v-model="selectedOption"
				:name="name"
				class="peer relative flex h-14 w-full select-none appearance-none items-center truncate rounded border-none bg-white pl-4 pr-10 outline outline-1 -outline-offset-1 focus-visible:outline-[3px] focus-visible:-outline-offset-[3px] focus-visible:outline-black-primary"
				:class="{
					'text-gray-70': !customOptionsList_isOpen && !disabled && !hasError && !isPopulated,
					'text-gray-80 outline-gray-70': !customOptionsList_isOpen && !disabled && !hasError,
					'text-black-primary outline-[3px] outline-black-primary': customOptionsList_isOpen && !hasError,
					'outline-error focus-visible:outline-error': hasError,
					'focus-visible:text-black-primary supportsHover:hover:text-black-primary supportsHover:hover:outline-black-primary':
						!disabled && !hasError,
					'cursor-pointer': !disabled,
					'pointer-events-auto cursor-not-allowed text-gray-50': disabled,
				}"
				style="-webkit-tap-highlight-color: transparent"
				:disabled="disabled"
				:required="required"
				:[dataTestName]="''"
				:aria-describedby="ariaDescribedby"
				@mousedown="handleMousedownOnSelect"
			>
				<option v-for="(optionValue, key) in generatedOptions" :key="optionValue" :value="key">
					{{ optionValue }}
				</option>
			</select>
			<label
				:for="name"
				class="pointer-events-none absolute -top-2.5 left-3 whitespace-nowrap bg-white px-1 text-sm"
				:class="{
					'text-gray-80': !customOptionsList_isOpen && !disabled && !hasError,
					'text-black-primary': customOptionsList_isOpen && !disabled && !hasError,
					'peer-focus-visible:text-black-primary supportsHover:peer-hover:text-black-primary': !disabled && !hasError,
					'text-gray-50': disabled,
					'text-error': hasError,
				}"
			>
				{{ label }}
			</label>
			<SvgIcon
				class="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 transition-transform"
				:class="{
					'rotate-180': customOptionsList_isOpen,
					'text-black-primary': isPopulated && !disabled,
					'text-error': hasError,
					'text-gray-50': disabled,
				}"
				icon-name="arrow-down-thin"
			/>
			<div
				class="absolute left-0 top-15 z-20 w-full rounded bg-white text-black-primary shadow-4"
				:class="{ hidden: !customOptionsList_isOpen || !isCustomDropdown }"
				style="clip-path: inset(0 -10px -10px)"
			>
				<ul ref="customOptionsList" class="max-h-[50vh] overflow-y-auto overscroll-contain rounded">
					<li v-for="(option, key, i) in generatedOptions" :key="key">
						<button
							type="button"
							class="flex h-12 w-full select-none items-center bg-white px-4 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 supportsHover:hover:bg-gray-40"
							style="-webkit-tap-highlight-color: transparent"
							:data-key="key"
							tabindex="-1"
							@click="customDropDown_select(key)"
						>
							<SvgIcon
								v-if="generatedIcons && generatedIcons[i]"
								class="mr-2 size-5 shrink-0"
								:icon-name="(generatedIcons[i] as SpriteSymbol)"
							/>
							<img
								v-else-if="generatedImageUrls && generatedImageUrls[i]"
								class="mr-2 size-5 shrink-0"
								:src="generatedImageUrls[i]"
								alt=""
							/>
							<span class="grow text-left leading-none" :class="{ 'ml-7': generatedIcons && !generatedIcons[i] }">
								{{ option }}
							</span>
						</button>
					</li>
				</ul>
			</div>
		</div>
		<HintText v-if="hint && (hintPermanent || !isValidationVisible)" no-indent :hint-text="hint" />
		<ValidationText v-if="isValidationVisible" no-indent :data-test-name="dataTestName" :error-message="errorMessage" />
	</div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, computed, nextTick, watch, toRef } from "vue";
import SvgIcon from "./SvgIcon/SvgIcon.vue";
import HintText from "./SharedForm/HintText.vue";
import ValidationText from "./SharedForm/ValidationText.vue";
import { useBreakpoints, onClickOutside, onKeyStroke } from "@vueuse/core";
import useInputValidation from "../composables/use-input-validation";
import type { HTMLInputElementEvent, InputEventDetails } from "../types/SharedInput.type";
import type { SpriteSymbol } from "../types/GeneratedSpriteType";

const props = withDefaults(
	defineProps<{
		disabled?: boolean;
		displayName?: string;
		hint?: string;
		hintPermanent?: boolean;
		icons?: string[];
		imageUrls?: string[];
		label: string;
		modelValue?: string | null;
		name: string;
		options?: { [key: string | number]: string };
		placeholder?: string;
		required?: boolean;
		value?: string;
		forcedErrorMessage?: string;
		requiredErrorMessage?: string;
		dataTestName?: string;
		ariaDescribedby?: string;
	}>(),
	{
		options: () => ({}),
		dataTestName: "",
	}
);

const emit = defineEmits<{
	(event: "update:modelValue", value: string | null): void;
	(e: "change-error-msg", event: HTMLInputElementEvent, details: InputEventDetails): void;
}>();

const nativeSelectRef = useTemplateRef("nativeSelect");
const dropDownWrapperRef = useTemplateRef("dropDownWrapper");
const customOptionsListRef = useTemplateRef("customOptionsList");

const isEm64 = useBreakpoints({ em64: "64em" }).greaterOrEqual("em64");
const isCustomDropdown = computed(() => isEm64.value);
const customOptionsList_isOpen = ref(false);
const customOptionsList_focusedOptionKey = ref<string | null | undefined>(null);
const hasPlaceholder = !!props.placeholder;
const generatedOptions = hasPlaceholder ? { ...{ "": props.placeholder }, ...props.options } : props.options;
const generatedIcons = hasPlaceholder && props.icons ? [""].concat(props.icons) : undefined;
const generatedImageUrls = hasPlaceholder && props.imageUrls ? [""].concat(props.imageUrls) : undefined;

const { isValidationVisible, errorMessage, handleInput } = useInputValidation(
	nativeSelectRef,
	props.displayName || props.name || "Feld",
	undefined,
	toRef(props, "forcedErrorMessage"),
	props.requiredErrorMessage
);

watch([isValidationVisible, errorMessage], () => {
	emit("change-error-msg", event as HTMLInputElementEvent, { errorMessage: errorMessage.value });
});

const selectedOption =
	props.modelValue === undefined
		? ref(props.value || "")
		: computed({
				get() {
					return props.modelValue;
				},
				set(val) {
					emit("update:modelValue", val || "");
				},
		  });

const hasError = computed(() => isValidationVisible.value);
const isPopulated = computed(() => !!selectedOption.value);

// Methods
const handleMousedownOnSelect = (e: Event) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	customDropDown_toggle();
};

const customDropDown_focusSelected = (key: string | null | undefined) => {
	if (key === null || key === undefined) return;
	customOptionsListRef.value?.querySelector<HTMLButtonElement>(`button[data-key="${key}"]`)?.focus();
};

const customDropDown_close = () => {
	customOptionsList_isOpen.value = false;
};

const customDropDown_toggle = () => {
	customOptionsList_isOpen.value = !customOptionsList_isOpen.value;
	if (!isCustomDropdown.value) return;

	if (customOptionsList_isOpen.value && selectedOption.value)
		nextTick(() => {
			customOptionsList_focusedOptionKey.value = selectedOption.value;
			customDropDown_focusSelected(customOptionsList_focusedOptionKey.value);
		});
};

const focusNativeSelect = () => {
	nextTick(() => nativeSelectRef.value?.focus());
};

const customDropDown_select = (key: string | number) => {
	if (key === "none") {
		key = "";
	}
	selectedOption.value = String(key);
	customDropDown_close();
	focusNativeSelect();
};

const handleSpaceAndEnter = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	customDropDown_toggle();
};

const handleArrowUpAndDown = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	if (!customOptionsList_isOpen.value) return customDropDown_toggle();

	const optionsKeys = Object.keys(generatedOptions);
	const focusedIndex = optionsKeys.indexOf(customOptionsList_focusedOptionKey.value || "");
	const add = e.key === "ArrowUp" ? -1 : 1;
	const newIndex = Math.min(Math.max(0, focusedIndex + add), optionsKeys.length - 1);
	customOptionsList_focusedOptionKey.value = optionsKeys[newIndex];

	customDropDown_focusSelected(customOptionsList_focusedOptionKey.value);
};

const handleTap = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	if (customOptionsList_isOpen.value) {
		e.preventDefault();
		const key = document.activeElement?.getAttribute("data-key");
		if (key) customDropDown_select(key);
		customDropDown_close();
		focusNativeSelect();
	}
};

const handleEscape = () => {
	customDropDown_close();
	focusNativeSelect();
};

onKeyStroke([" ", "Spacebar", "Enter"], (e) => handleSpaceAndEnter(e), {
	target: nativeSelectRef,
});

onKeyStroke(["ArrowUp", "ArrowDown"], (e: KeyboardEvent) => handleArrowUpAndDown(e), {
	target: dropDownWrapperRef,
});

onKeyStroke("Tab", (e: KeyboardEvent) => handleTap(e), { target: dropDownWrapperRef });

onKeyStroke("Escape", () => handleEscape(), { target: dropDownWrapperRef });

onClickOutside(dropDownWrapperRef, () => customDropDown_close());

watch(
	() => selectedOption.value,
	() => {
		customDropDown_close();
		handleInput();
	}
);
</script>
